import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import {
    DeselectSelectedModules,
    FetchAllModulesFailure,
    FetchAllModulesSuccess,
    SelectFilteredModules,
    SortAllModules,
    ToggleSelectedModule,
} from 'app/library/all-modules/actions';
import { DeleteModule } from 'app/library/module/actions';
import { ModuleStatuses } from 'app/library/module/models/module-statuses';
import { Module } from 'app/library/module/models/module.model';
import { ModuleGlobalService } from 'app/library/module/services/module-global.service';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { IStatus } from 'app/projects/shared/src/lib/interfaces/status.interface';
import { PageSizePreferenceService } from 'app/projects/shared/src/lib/services/page-size-preference.service';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { ModuleManagersService } from '../../../module-managers/services/module-managers.service';
import { AllModulesState } from '../../models/all-modules.state';
import { AllModuleDataSource } from './datasource';

@Component({
    selector: 'app-module-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AllModuleListComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
    @Input()
    sortBy: string;

    @Input()
    sortOrder: SortDirection;

    @Input()
    loadModule$: Observable<boolean>;

    @Input()
    filteredModule$: Observable<Module[]>;

    @Input()
    module$: Observable<Module[]>;

    @Input()
    selectedModuleId$: Observable<string[]>;

    @Input()
    searchTerm$: Observable<string>;
    searchTerm: string;

    @Input()
    totalCount$: Observable<number>;

    @Input()
    tag$: Observable<MaxBrainTag[]>;
    tagIds: string[] = [];

    @Input()
    statuse$: Observable<IStatus>;
    status: IStatus;

    @Input()
    removeModuleFromList$: Observable<Module>;

    @Input()
    profile: User;

    @Input()
    kind: string;

    @Input()
    sessionStoragePrefix: 'allModules' | 'allExecutions';

    get pageSizePreferenceKey(): string {
        return this.sessionStoragePrefix === 'allExecutions' ? 'all-modules.executions-list' : 'all-modules.list';
    }

    @Input()
    displayedColumns: string[];

    @Input()
    context: string;

    @Input()
    disableSelectionForListedExecutions: boolean = false;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;
    @ViewChild('chipList')
    chipList: ElementRef;

    @Output()
    clickedTag = new EventEmitter<MaxBrainTag>();

    @Output()
    updateList = new EventEmitter<Module[]>();

    dataSource: AllModuleDataSource;
    selectedModuleIds: string[];
    checkboxes: {};
    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    moduleStatuses = ModuleStatuses;
    isLoading: boolean = false;
    list: Module[] = [];
    readonly pageSizeOptions = [10, 25, 50];
    pageSize = 10;
    private _didInitialLoad = false;

    constructor(
        private _store: Store,
        private _action$: Actions,
        public matDialog: MatDialog,
        private _moduleGlobalService: ModuleGlobalService,
        private _translateService: TranslateService,
        private _moduleManagersService: ModuleManagersService,
        private _cd: ChangeDetectorRef,
        private _pageSizePreferenceService: PageSizePreferenceService
    ) {
        super();
    }

    ngOnInit(): void {
        this.loadModule$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((resetPageIndex) => resetPageIndex !== null)
            )
            .subscribe((resetPageIndex) => {
                this.loadModulesPage(resetPageIndex);
            });

        this.module$.pipe(takeUntil(this._unsubscribeAll)).subscribe((modules) => {
            this.checkboxes = {};

            if (modules) {
                modules.map((module) => {
                    this.checkboxes[module.id] = false;
                });
            }
        });

        this.selectedModuleId$.pipe(takeUntil(this._unsubscribeAll)).subscribe((selectedModuleIds) => {
            for (const id in this.checkboxes) {
                if (!this.checkboxes.hasOwnProperty(id)) {
                    continue;
                }

                this.checkboxes[id] = selectedModuleIds.includes(id);
            }

            this.selectedModuleIds = selectedModuleIds;
        });

        if (this.removeModuleFromList$) {
            this.removeModuleFromList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((module: Module) => {
                if (module) {
                    this.removeFromList(module);
                }
            });
        }

        this.pageSize = this._pageSizePreferenceService.getPageSize(this.pageSizePreferenceKey, this.pageSizeOptions, 10);

        setTimeout(() => {
            if (!this.paginator) {
                return;
            }

            this.pageSize = this._pageSizePreferenceService.getPageSize(this.pageSizePreferenceKey, this.pageSizeOptions, 10);
            this.paginator.pageSize = this.pageSize;

            // Dialogs should always start on page 1; main list can restore last page index.
            this.paginator.pageIndex = this.removeModuleFromList$ ? 0 : this._store.selectSnapshot(AllModulesState.getPageIndex);

            this.paginator.page.subscribe(({ length, pageIndex, pageSize }: PageEvent) => {
                this.pageSize = pageSize;

                const itemsleft = length - pageIndex * pageSize;
                const itemsInThisPage = itemsleft < pageSize ? itemsleft : pageSize;
                // FIXME: these hardcoded numbers represent the height of the rest of the elements surrounding the table within the layout content
                const tableHeight = 80 + 24 + 64 + 56 + itemsInThisPage * 65 + 72 + 56;
                const contentElementHeight = document.getElementById('container-2')?.offsetHeight;
                if (!contentElementHeight) {
                    return;
                }
                const scrollTop = tableHeight > contentElementHeight ? tableHeight - contentElementHeight : 0;

                setTimeout(() => {
                    const container = document.getElementById('container-3');
                    if (container) {
                        container.scrollTop = scrollTop;
                    }
                });
            });
        });

        // TODO: Fix _loadingSubject in datasource and remove this subscription
        this._action$.pipe(ofActionDispatched(FetchAllModulesSuccess, FetchAllModulesFailure), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.isLoading = false;
        });
    }

    ngAfterViewInit(): void {
        this.dataSource = new AllModuleDataSource(this.filteredModule$, this._store, this._action$);
        this._cd.detectChanges();

        this.statuse$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((selectedStatus, index) => {
                    this.status = selectedStatus;
                    return index > 0;
                })
            )
            .subscribe(() => {
                this.loadModulesPage(true);
            });

        this.tag$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((selectedTags, index) => {
                    this.tagIds = selectedTags ? selectedTags.map((tag) => tag.id) : [];
                    return index > 0;
                })
            )
            .subscribe(() => {
                this.loadModulesPage(true);
            });

        this.searchTerm$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((searchTerm, index) => {
                    this.searchTerm = searchTerm || '';
                    return index > 0;
                })
            )
            .subscribe(() => {
                this.loadModulesPage(true);
            });

        this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
            this._store.dispatch(new SortAllModules(sort));

            this.loadModulesPage(true);
        });

        this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe((event: PageEvent) => {
            this.pageSize = event.pageSize;
            this.loadModulesPage(false);
        });

        // Same pattern as all-users: always trigger the first fetch.
        // persistPageSize only emits when stored size differs from the template default.
        setTimeout(() => {
            if (this._didInitialLoad || !this.paginator) {
                return;
            }

            this._didInitialLoad = true;
            const preferredPageSize = this._pageSizePreferenceService.getPageSize(this.pageSizePreferenceKey, this.pageSizeOptions, 10);
            this.pageSize = preferredPageSize;

            if (this.paginator.pageSize !== preferredPageSize) {
                this.paginator._changePageSize(preferredPageSize);
            } else {
                this.loadModulesPage(!!this.removeModuleFromList$);
            }
        });
    }

    ngAfterViewChecked(): void {
        this._cd.detectChanges();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    removeFromList(module: Module): void {
        this.list = [...this.list.filter((item) => item.id !== module.id)];

        this.dataSource = new AllModuleDataSource(
            this.filteredModule$.pipe(
                map((modules) => {
                    return modules.map((item) => {
                        if (item.id === module.id) {
                            item = Object.assign(new User(), item, {
                                selected: false,
                            });
                        } else if (this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );

        this.updateList.emit(this.list);
    }

    addToList(user: Module): void {
        this.list = [...this.list, user];
        this.dataSource = new AllModuleDataSource(
            this.filteredModule$.pipe(
                map((modules) => {
                    return modules.map((item) => {
                        if (item.id === user.id || this.list.find((item2) => item2.id === item.id)) {
                            item = Object.assign(new User(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );

        this.updateList.emit(this.list);
    }

    isModuleSelectable(module: Module): boolean {
        if (!this.disableSelectionForListedExecutions) {
            return true;
        }

        if (module?.selectionStatus) {
            return module.selectionStatus.isSelectable;
        }

        return !module?.listingId;
    }

    visualLength(text: string): number {
        const ruler = document.getElementById('ruler');
        ruler.innerHTML = text;
        const toReturn = ruler.offsetWidth;
        ruler.innerHTML = '';
        return toReturn;
    }

    /**
     * Edit module
     *
     * @param {Module} module
     */
    editModule(module: Module): void {
        this._store.dispatch(new Navigate([`/${Module.label_plural}/${module.id}/edit`]));
    }

    /**
     * Delete module
     *
     * @param {Module} moduleToDelete
     */
    deleteModule(moduleToDelete: Module): void {
        this.confirmDialogRef = this.matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('MODULE.MESSAGE.DELETE_MODULE_CONFIRMATION', { moduleName: moduleToDelete.name });
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.DELETE';

        this.confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this._store.dispatch(new DeleteModule(moduleToDelete.id));
            }

            this.confirmDialogRef = null;
        });
    }

    /**
     * Go to module
     *
     * @param {Module} module
     */
    goToModule(module: Module): void {
        this._moduleGlobalService.setParentUrl(window.location.pathname);
        this._moduleManagersService.goToModule(this.profile.id, module, 'overview');
    }

    /**
     * On selected change
     *
     * @param {string} moduleId
     */
    onSelectedChange(moduleId: string): void {
        this._store.dispatch(new ToggleSelectedModule(moduleId));
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected(): boolean {
        return this.selectedModuleIds.length === this.dataSource.connect.length;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle(): void {
        this._store.dispatch(this.isAllSelected() ? new DeselectSelectedModules() : new SelectFilteredModules());
    }

    filterByTag(tag: MaxBrainTag): void {
        this.clickedTag.emit(tag);
    }

    loadModulesPage(resetPageIndex = false): void {
        this.isLoading = true;

        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        const pageSize = this.paginator?.pageSize || this.pageSize;

        this.dataSource.loadModules(
            { tags: this.tagIds, status: this.status ? [this.status.name] : [] },
            this.searchTerm,
            this.sort.active,
            this.sort.direction,
            pageSize,
            this.paginator.pageIndex + 1,
            false,
            this.kind,
            this.context
        );
    }
}
